/**
 * Pure bell vs sidebar indicator counts — single source for hooks and Phase 4 smoke tests.
 */
import {
  getSidebarPageForNotificationType,
  isOperationsNotificationType,
  type NotificationRole,
} from '../constants/notificationChannels';
import {
  BrokerShipperSidebarPage,
  CarrierSidebarPage,
  getBrokerShipperSidebarPageForType,
  normalizeNotificationCreatedAt,
} from '../constants/notificationSidebarAllocation';
import { countUnreadBellTrayNotifications } from '../constants/notificationTrayUi';
import {
  isDashboardActionRequiredNotificationType,
} from '../constants/notificationTypes';
import { isNewerThanPageVisit } from './notificationTrackingService';
import { isSidebarOpsNotificationActionable } from './sidebarOpsNotificationHygiene';

export interface IndicatorNotificationLike {
  id?: string;
  type: string;
  read?: boolean;
  createdAt?: Date | unknown;
  poNumber?: string;
}

export interface BrokerShipperIndicatorCounts {
  purchaseOrders: number;
  shippingSchedule: number;
  driverUpdates: number;
  carrierPartners: number;
  payments: number;
  shipmentArchive: number;
  bellOnly: number;
  total: number;
}

export interface CarrierIndicatorCounts {
  myLoads: number;
  availableLoads: number;
  partners: number;
  payments: number;
  bellOnly: number;
  total: number;
}

export const EMPTY_BROKER_SHIPPER_COUNTS: BrokerShipperIndicatorCounts = {
  purchaseOrders: 0,
  shippingSchedule: 0,
  driverUpdates: 0,
  carrierPartners: 0,
  payments: 0,
  shipmentArchive: 0,
  bellOnly: 0,
  total: 0,
};

export const EMPTY_CARRIER_COUNTS: CarrierIndicatorCounts = {
  myLoads: 0,
  availableLoads: 0,
  partners: 0,
  payments: 0,
  bellOnly: 0,
  total: 0,
};

function getBrokerShipperSidebarCategory(type: string): BrokerShipperSidebarPage | null {
  if (isDashboardActionRequiredNotificationType(type)) {
    return null;
  }
  return getBrokerShipperSidebarPageForType(type);
}

function toPageVisitNotification(n: IndicatorNotificationLike & { createdAt: Date }) {
  return {
    id: n.id ?? '',
    type: n.type,
    read: n.read ?? false,
    createdAt: n.createdAt,
    poNumber: n.poNumber,
  };
}

function normalizeForCounting(notifications: IndicatorNotificationLike[]) {
  return notifications.map((n) => ({
    ...n,
    createdAt: normalizeNotificationCreatedAt(n.createdAt),
  }));
}

export function computeBrokerShipperIndicatorCounts(
  role: Extract<NotificationRole, 'broker' | 'shipper'>,
  notifications: IndicatorNotificationLike[],
  pageVisits: Partial<Record<BrokerShipperSidebarPage | 'dashboard', Date | null>>,
  poActiveMap: Map<string, boolean>,
  now: Date = new Date()
): BrokerShipperIndicatorCounts {
  if (notifications.length === 0) {
    return { ...EMPTY_BROKER_SHIPPER_COUNTS };
  }

  const normalized = normalizeForCounting(notifications);
  const bellOnly = countUnreadBellTrayNotifications(
    normalized.map((n) => ({ type: n.type, read: n.read, createdAt: n.createdAt as Date })),
    role,
    now
  );

  const counts: BrokerShipperIndicatorCounts = {
    ...EMPTY_BROKER_SHIPPER_COUNTS,
    bellOnly,
  };

  normalized.forEach((n) => {
    if (!isOperationsNotificationType(n.type, role)) return;

    const category = getBrokerShipperSidebarCategory(n.type);
    if (!category) return;

    if (!isSidebarOpsNotificationActionable(n, poActiveMap)) return;

    const lastVisit = pageVisits[category] ?? null;
    if (!isNewerThanPageVisit(toPageVisitNotification(n as IndicatorNotificationLike & { createdAt: Date }), lastVisit)) return;

    counts.total++;
    counts[category]++;
  });

  return counts;
}

export function computeCarrierIndicatorCounts(
  notifications: IndicatorNotificationLike[],
  pageVisits: Partial<Record<CarrierSidebarPage, Date | null>>,
  now: Date = new Date()
): CarrierIndicatorCounts {
  if (notifications.length === 0) {
    return { ...EMPTY_CARRIER_COUNTS };
  }

  const normalized = normalizeForCounting(notifications);
  const bellOnly = countUnreadBellTrayNotifications(
    normalized.map((n) => ({ type: n.type, read: n.read, createdAt: n.createdAt as Date })),
    'carrier',
    now
  );

  const counts: CarrierIndicatorCounts = {
    ...EMPTY_CARRIER_COUNTS,
    bellOnly,
  };

  normalized.forEach((n) => {
    if (!isOperationsNotificationType(n.type, 'carrier')) return;

    const category = getSidebarPageForNotificationType(n.type, 'carrier') as CarrierSidebarPage | null;
    if (!category) return;

    const lastVisit = pageVisits[category] ?? null;
    if (!isNewerThanPageVisit(toPageVisitNotification(n as IndicatorNotificationLike & { createdAt: Date }), lastVisit)) return;

    counts.total++;
    counts[category]++;
  });

  return counts;
}

/** True when a type cannot appear on both bell and sidebar for the same role. */
export function isChannelExclusive(type: string, role: NotificationRole): boolean {
  const isOps = isOperationsNotificationType(type, role);
  const isBell =
    !isDashboardActionRequiredNotificationType(type) &&
    countUnreadBellTrayNotifications([{ type, read: false, createdAt: new Date() }], role) > 0;
  return !(isOps && isBell);
}
